import { create } from "zustand";
import axiosInstance from "../common/axiosInstance";
import { handleApiMsg } from "../common/utils";
import { toast } from "react-toastify";
export type KPIStatus = "draft" | "in_progress" | "submitted" | "locked";

export interface TemplateType {
  id: string;
  name: string;
  description: string;
  version: number;
  questionCount: number;
  createdAt: string;
  active: boolean;
}

export interface TemplateItemType {
  id: string;
  srNo: number;
  section: string;
  question: string;
  order: number;
}

export interface FileAttachment {
  fileKey: string;
  name: string;
  size: number;
  mime: string;
  url: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface AssessmentType {
  id: string;
  name: string;
  month?: string; // YYYY-MM
  premiseId?: string;
  date?: string;
  templateId?: string;
  templateVersion?: number;
  status?: KPIStatus;
  compliancePct?: number;
  totalItems?: number;
  compliedPct?: number;
  notCompliedPct?: number;
  partiallyCompliedPct?: number;
  createdAt?: string;
  attachments?: FileAttachment[];
}

export interface AssessmentItemType {
  id: string;
  srNo: number;
  section: string;
  question: string;
  status?: string;
  remarks: string;
}

interface KPIState {
  loading: boolean;
  error: boolean;

  // Templates
  templates: TemplateType[];
  templateItems: TemplateItemType[];
  fetchAllTemplates: () => Promise<boolean>;
  fetchTemplateItems: (templateId: string) => Promise<boolean>;
  importTemplate: (payload: {
    file: File;
    name: string;
    description?: string;
  }) => Promise<boolean>;

  // Assessments
  assessments: AssessmentType[];
  assessment: AssessmentType | null;
  assessmentItems: AssessmentItemType[];
  fetchAllAssessments: () => Promise<boolean>;
  fetchAssessmentsPaged: (params: {
    page: number;
    limit: number;
    name?: string;
    status?: string;
    premiseId?: string;
    date?: string;
  }) => Promise<{ data: any[]; count: number } | null>;
  createAssessment: (payload: {
    name: string;
    date: string;
    premiseId: string;
  }) => Promise<any>;

  fetchAssessment: (id: string) => Promise<void>;
  fetchAssessmentItems: (assessmentId: string) => Promise<any[]>;
  updateAssessmentItem: (
    assessmentId: string,
    srNo: number,
    payload: {
      status?: "" | "Complied" | "Not Complied" | "NA";
      remarks?: string;
    }
  ) => Promise<boolean>;
  submitAssessment: (assessmentId: string) => Promise<boolean>;
  exportAssessment: (assessmentId: string) => Promise<Blob | null>;
  uploadAssessmentAttachment: (
    assessmentId: string,
    file: File
  ) => Promise<any>;
}

export const useKPIStore = create<KPIState>((set, get) => ({
  loading: false,
  error: false,

  templates: [],
  templateItems: [],
  assessments: [],
  assessment: null,
  assessmentItems: [],

  fetchAllTemplates: async () => {
    set({ loading: true, error: false });
    try {
      const res = await axiosInstance.get(`/api/kpi/templates`);
      const templates = res?.data?.data || [];
      set({ templates, loading: false, error: false });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  fetchTemplateItems: async (templateId: string) => {
    set({ loading: true, error: false, templateItems: [] });
    try {
      const res = await axiosInstance.get(
        `/api/kpi/templates/${templateId}/items?page=1&limit=500`
      );
      const templateItems = res?.data?.data || [];
      set({ templateItems, loading: false, error: false });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  importTemplate: async ({ file, name, description }) => {
    set({ loading: true, error: false });
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("name", name);
      if (description) form.append("description", description);

      await axiosInstance.post(`/api/kpi/templates/import`, form);

      await get().fetchAllTemplates();
      set({ loading: false, error: false });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  fetchAllAssessments: async () => {
    set({ loading: true, error: false });
    try {
      const res = await axiosInstance.get(`/api/kpi/assessments?page=1&limit=50`);
      const assessments = res?.data?.data || res?.data || [];
      set({ assessments, loading: false, error: false });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  fetchAssessmentsPaged: async (params: {
    page: number;
    limit: number;
    name?: string;
    status?: string;
    premiseId?: string;
    date?: string;
  }) => {
    set({ loading: true, error: false });
    try {
      const res = await axiosInstance.get(`/api/kpi/assessments`, { params });
      const data = res?.data?.data || res?.data;
      const docs = data?.data || data?.docs || data || [];
      const count = res?.data?.meta?.count ?? data?.count ?? 0;
      const normalized = docs.map((d: any) => ({ ...d, id: d.id || d._id }));
      set({ assessments: normalized, loading: false, error: false });
      return { data: normalized, count };
    } catch (e) {
      set({ loading: false, error: true });
      return { data: [], count: 0 };
    }
  },
  //       });
  //       await get().fetchAllAssessments();
  //       set({ loading: false, error: false });
  //       return true;
  //     } catch (e) {
  //       set({ error: true, loading: false });
  //       return false;
  //     }
  //   },

  createAssessment: async (payload: {
    name: string;
    date: string;
    premiseId: string;
  }) => {
    set({ loading: true, error: false });
    try {
      const res = await axiosInstance.post(
        `/api/kpi/assessments`,
        payload
      );
      set({ loading: false, error: false });
      return res.data;
    } catch (e) {
      set({ error: true, loading: false });
      throw e;
    }
  },

  // In
  fetchAssessment: async (id: string) => {
    set({ loading: true, error: false });
    try {
      const res = await axiosInstance.get(`/api/kpi/assessments/${id}`);
      const data = res?.data?.data || res?.data;
      set((state: any) => ({
        ...state,
        assessment: data,
        loading: false,
      }));
    } catch (e) {
      set((state: any) => ({
        ...state,
        loading: false,
        error: true,
        assessment: null,
      }));
    }
  },

  fetchAssessmentItems: async (assessmentId: string) => {
    set({ loading: true, error: false });
    try {
      const res = await axiosInstance.get(
        `/api/kpi/assessments/${assessmentId}/items`,
        { params: { page: 1, limit: 2000 } }
      );
      const data = res?.data?.data || res?.data;
      set((state: any) => ({
        ...state,
        assessmentItems: data,
        loading: false,
      }));
      return data;
    } catch (e) {
      set((state: any) => ({
        ...state,
        loading: false,
        error: true,
        assessmentItems: [],
      }));
      return [];
    }
  },

  updateAssessmentItem: async (
    assessmentId: string,
    srNo: number,
    patch: { status?: string; remarks?: string }
  ) => {
    await axiosInstance.put(
      `/api/kpi/assessments/${assessmentId}/items/${srNo}`,
      patch
    );

    set((state: any) => ({
      ...state,
      assessmentItems: Array.isArray(state.assessmentItems)
        ? state.assessmentItems.map((item: any) =>
            item.srNo === srNo ? { ...item, ...patch } : item
          )
        : [],
    }));
    return true;
  },

  submitAssessment: async (assessmentId: string) => {
    try {
      const res = await axiosInstance.post(
        `/api/kpi/assessments/${assessmentId}/submit`,
        {}
      );
      set((state: any) => ({
        ...state,
        assessment: { ...state.assessment, status: "submitted" },
        loading: false,
        error: false,
      }));
      return res?.data;
    } catch (e) {
      set((state: any) => ({
        ...state,
        loading: false,
        error: true,
      }));
      throw e;
    }
  },

  uploadAssessmentAttachment: async (assessmentId: string, file: File) => {
    try {
      set({ loading: true, error: false });

      const form = new FormData();
      form.append("file", file);
      const res = await axiosInstance.post(
        `/api/kpi/assessments/${assessmentId}/attachments`,
        form
      );
      const att = res?.data?.data;
      set((s: any) => ({
        loading: false,
        error: false,
        assessment: s.assessment
          ? {
              ...s.assessment,
              attachments: [...(s.assessment.attachments || []), att],
            }
          : s.assessment,
      }));
      toast.success("File uploaded successfully");
      return att;
    } catch (e) {
      set({ loading: false, error: true });
      throw e;
    }
  },

  exportAssessment: async (assessmentId: string) => {
    try {
      const res = await axiosInstance.get(
        `/api/kpi/assessments/${assessmentId}/export`,
        {
          responseType: "blob",
        }
      );
      set({ loading: false, error: false });
      return res.data as Blob;
    } catch (e) {
      set({ loading: false, error: true });
      return null;
    }
  },
}));
